import { jsxs, jsx } from 'react/jsx-runtime';
import Divider from '@fabric-app/ui/components/Divider';
import { ScrollArea } from '@fabric-app/ui/components/ScrollArea';
import Section from '@fabric-app/ui/components/Section';
import { P, SPAN } from '@fabric-app/ui/components/Typography';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { styled } from 'styled-components';
import { useAssistantStore, useAssistantContext } from '../stores/assistantStore.js';
import { Flex } from '@fabric-app/ui/components/Flex';
import { renderResourceIcon } from '../../resource-detail/components/ResourceIcon/renderResourceIcon.js';
import { shallow as shallow$1 } from '../../../node_modules/zustand/esm/shallow.js';
import { useQueryResourceDetail } from '../../resources/queries/useQueryResourceDetail.js';
import Skeleton from '@fabric-app/ui/components/Skeleton';
import { mediaHover, mediaMobile } from '@fabric-app/ui/constants/styled-utils';
import { ButtonIcon } from '@fabric-app/ui/components/Button';
import { IconDesktop16Cross, IconDesktop16KindHighlight } from '@fabric-app/icons/svg';
import { getResourceNamePlaceholder } from '../../resources/utils/getResourceTitle.js';
import { pick } from '@fabric-app/utils/object/manipulation';
import * as Popover from '@radix-ui/react-popover';
import { useShowExpandedResource } from '../../resources/stores/useExpandedResourceStore.js';
import Tooltip from '@fabric-app/ui/components/Tooltip';
import { useMemo } from 'react';

const AssistantContextCard = styled(Section).attrs((p) => {
  return {
    variant: "simple",
    ...p
  };
}).withConfig({
  displayName: "AssistantContext__AssistantContextCard",
  componentId: "sc-49d7cfcd-0"
})([
  `display:flex;flex-direction:column;gap:0.75rem;background-color:`,
  `;max-height:260px;overflow:hidden;min-width:0;max-width:100%;width:318px;padding:0;color:`,
  `;z-index:126;pointer-events:auto;&:focus-visible{outline:none;}border:1px solid `,
  `;box-shadow:0px 4px 100px 0px rgba(var(--fabric-color-bg-primary-reverse-rgb),0.05);`
], cssVar["color-bg-primary"], cssVar["color-text-tertiary-on-light"], cssVar["color-border-primary"]);
const AssistantContextCardHeader = styled(Flex).attrs((p) => {
  return {
    direction: "column",
    gap: 12,
    ...p
  };
}).withConfig({
  displayName: "AssistantContext__AssistantContextCardHeader",
  componentId: "sc-49d7cfcd-1"
})([
  `padding:0.75rem;&:not(:last-child){padding-bottom:0;}`
]);
const AssistantContextCardTitle = styled(P).attrs((p) => {
  return {
    size: "sm-plus",
    weight: 600,
    color: "inherit",
    ...p
  };
}).withConfig({
  displayName: "AssistantContext__AssistantContextCardTitle",
  componentId: "sc-49d7cfcd-2"
})([
  `line-height:normal;`
]);
const AssistantContextCardDescription = styled(P).attrs((p) => {
  return {
    size: "sm-plus",
    weight: 400,
    color: "inherit",
    ...p
  };
}).withConfig({
  displayName: "AssistantContext__AssistantContextCardDescription",
  componentId: "sc-49d7cfcd-3"
})([
  `line-height:normal;`
]);
const AssistantContextContent = styled(Flex).attrs((p) => {
  return {
    direction: "column",
    ...p
  };
}).withConfig({
  displayName: "AssistantContext__AssistantContextContent",
  componentId: "sc-49d7cfcd-4"
})([
  `color:inherit;padding:0.75rem;padding-top:0;`
]);
const AssistantContextItemRemoveButton = styled(ButtonIcon).attrs((p) => {
  return {
    ...p,
    size: "xss",
    variant: "transparent"
  };
}).withConfig({
  displayName: "AssistantContext__AssistantContextItemRemoveButton",
  componentId: "sc-49d7cfcd-5"
})([
  `height:20px;width:20px;margin-right:-4px;display:none;color:`,
  `;`,
  `{&:hover{color:inherit;}}`,
  `{display:flex;}`
], cssVar["color-text-quaternary"], mediaHover, mediaMobile);
const AssistantContextItemContainer = styled(Flex).attrs((p) => {
  return {
    gap: "element",
    alignItems: "center",
    ...p
  };
}).withConfig({
  displayName: "AssistantContext__AssistantContextItemContainer",
  componentId: "sc-49d7cfcd-6"
})([
  `padding:0.375rem 0.5rem;border-radius:0.375rem;`,
  `{&:hover{background-color:`,
  `;`,
  `{display:flex;}}}`
], mediaHover, cssVar["color-bg-tertiary"], AssistantContextItemRemoveButton);
const AssistantContextItemName = styled(P).attrs((p) => {
  return {
    size: "sm",
    weight: 500,
    color: "inherit",
    ...p
  };
}).withConfig({
  displayName: "AssistantContext__AssistantContextItemName",
  componentId: "sc-49d7cfcd-7"
})([
  `overflow:hidden;text-overflow:ellipsis;white-space:nowrap;word-break:keep-all;flex-grow:1;`
]);
const AssistantContextResourceItem = ({ resourceId, onRemove }) => {
  const { data: resource, isLoading } = useQueryResourceDetail(resourceId);
  const showExpandedResource = useShowExpandedResource();
  if (isLoading || !resource) {
    return /* @__PURE__ */ jsxs(AssistantContextItemContainer, {
      children: [
        /* @__PURE__ */ jsx(Skeleton, {
          borderRadius: 8,
          height: 16,
          width: 16
        }),
        /* @__PURE__ */ jsx(Skeleton, {
          borderRadius: 8,
          height: 16,
          width: 100
        })
      ]
    });
  }
  return /* @__PURE__ */ jsxs(AssistantContextItemContainer, {
    onClick: () => showExpandedResource(resource),
    children: [
      renderResourceIcon(resource, {
        size: 16
      }),
      /* @__PURE__ */ jsxs(AssistantContextItemName, {
        children: [
          resource.name ?? getResourceNamePlaceholder(resource),
          resource.extension ? /* @__PURE__ */ jsxs(SPAN, {
            size: "inherit",
            color: "tertiary",
            children: [
              ".",
              resource.extension
            ]
          }) : null
        ]
      }),
      /* @__PURE__ */ jsx(AssistantContextItemRemoveButton, {
        onClick: (e) => {
          e.stopPropagation();
          onRemove(resourceId);
        },
        children: /* @__PURE__ */ jsx(IconDesktop16Cross, {})
      })
    ]
  });
};
const AssistantContextTextItem = ({ text, onRemove }) => {
  return /* @__PURE__ */ jsx(Tooltip, {
    label: text,
    children: /* @__PURE__ */ jsxs(AssistantContextItemContainer, {
      children: [
        /* @__PURE__ */ jsx(IconDesktop16KindHighlight, {}),
        /* @__PURE__ */ jsx(AssistantContextItemName, {
          children: text
        }),
        /* @__PURE__ */ jsx(AssistantContextItemRemoveButton, {
          onClick: (e) => {
            e.stopPropagation();
            onRemove(text);
          },
          children: /* @__PURE__ */ jsx(IconDesktop16Cross, {})
        })
      ]
    })
  });
};
const AssistantContextDefault = ({ children }) => {
  const { setPersistentContext } = useAssistantStore((s) => pick(s, [
    "persistentContext",
    "setPersistentContext"
  ]), shallow$1);
  const { context } = useAssistantContext();
  const persistentOnly = useMemo(() => {
    return context.filter((c) => c.type === "persistent");
  }, [
    context
  ]);
  if (!persistentOnly.length) {
    return null;
  }
  return /* @__PURE__ */ jsxs(Popover.Root, {
    children: [
      /* @__PURE__ */ jsx(Popover.Trigger, {
        asChild: true,
        children
      }),
      /* @__PURE__ */ jsx(Popover.Portal, {
        children: /* @__PURE__ */ jsx(Popover.Content, {
          align: "center",
          sideOffset: 10,
          asChild: true,
          onWheel: (e) => e.stopPropagation(),
          onTouchMove: (e) => e.stopPropagation(),
          collisionPadding: 12,
          children: /* @__PURE__ */ jsxs(AssistantContextCard, {
            children: [
              /* @__PURE__ */ jsxs(AssistantContextCardHeader, {
                children: [
                  /* @__PURE__ */ jsx(AssistantContextCardTitle, {
                    children: "Custom assistant context"
                  }),
                  /* @__PURE__ */ jsx(AssistantContextCardDescription, {
                    children: "You can add or remove multiple files or folders, to be included in the assistant’s awareness."
                  }),
                  /* @__PURE__ */ jsx(Divider, {})
                ]
              }),
              /* @__PURE__ */ jsx(ScrollArea.Default, {
                orientation: "vertical",
                scrollbarVariant: "subtle",
                expandVertically: true,
                innerDisplayFlex: true,
                asFlex: true,
                children: /* @__PURE__ */ jsx(AssistantContextContent, {
                  children: persistentOnly.map(({ value, subType }) => subType === "resourceId" ? /* @__PURE__ */ jsx(AssistantContextResourceItem, {
                    resourceId: value,
                    onRemove: () => {
                      setPersistentContext((prev) => ({
                        ...prev,
                        resourceIds: prev.resourceIds.filter((prevId) => prevId !== value)
                      }));
                    }
                  }, value) : /* @__PURE__ */ jsx(AssistantContextTextItem, {
                    text: value,
                    onRemove: () => setPersistentContext((prev) => ({
                      ...prev,
                      textSnippets: prev.textSnippets.filter((prevSnippet) => prevSnippet !== value)
                    }))
                  }, value))
                })
              })
            ]
          })
        })
      })
    ]
  });
};
const AssistantContext = {
  Default: AssistantContextDefault,
  Card: AssistantContextCard,
  Title: AssistantContextCardTitle,
  Description: AssistantContextCardDescription
};

export { AssistantContext, AssistantContextCard };
//# sourceMappingURL=AssistantContext.js.map
